// src/ui/screens/verifications/IdVerificationPage.jsx

import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { db, storage } from '../../../firebase';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { CategorySelectionPage, SellerCategoryType } from './CategorySelectionPage';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

// ── Icons ────────────────────────────────────────────────────────────────

const BackIcon = () => (
  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const WarningIcon = () => (
  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const ForwardIcon = () => (
  <svg className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

const LocationIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const EmailIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const ContactPageIcon = () => (
  <svg className="w-6 h-6" style={{ color: COLORS.orange }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4V4zM9 10a2 2 0 100-4 2 2 0 000 4zM6 17c0-2 1.5-3.5 3-3.5S12 15 12 17M14 8h4M14 12h4M14 16h3" />
  </svg>
);

const DescriptionIcon = () => (
  <svg className="w-6 h-6" style={{ color: COLORS.orange }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" style={{ color: COLORS.orange }} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

const VerifiedIcon = () => (
  <svg className="w-[22px] h-[22px] text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// ── IdVerificationPage ───────────────────────────────────────────────────

export const IdVerificationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryType, setSelectedCategoryType] = useState(null);

  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');

  // Personal: National ID images
  const [frontIdImage, setFrontIdImage] = useState(null);
  const [frontIdFile, setFrontIdFile] = useState(null);
  const [backIdImage, setBackIdImage] = useState(null);
  const [backIdFile, setBackIdFile] = useState(null);

  // Business & Organizations: Registration document/certificate
  const [registrationDocImage, setRegistrationDocImage] = useState(null);
  const [registrationDocFile, setRegistrationDocFile] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const regDocInputRef = useRef(null);

  const isPersonal = selectedCategoryType === SellerCategoryType.personal;
  const isBusiness = selectedCategoryType === SellerCategoryType.business;

  // ── Category selection ────────────────────────────────────────────────

  const handleCategorySelect = (category, categoryType) => {
    // Reset document uploads if the category type changed
    if (selectedCategoryType !== categoryType) {
      setFrontIdImage(null);
      setFrontIdFile(null);
      setBackIdImage(null);
      setBackIdFile(null);
      setRegistrationDocImage(null);
      setRegistrationDocFile(null);
    }
    setSelectedCategory(category);
    setSelectedCategoryType(categoryType);
    setShowCategoryPicker(false);
  };

  // ── Validate form ─────────────────────────────────────────────────────

  const validateForm = () => {
    if (!selectedCategory || !selectedCategoryType) return false;
    if (!address.trim()) return false;

    if (isPersonal) {
      if (!frontIdFile) return false;
      if (!backIdFile) return false;
    } else if (isBusiness) {
      if (!registrationDocFile) return false;
    } else {
      return false;
    }
    return true;
  };

  // ── Handle image pick ─────────────────────────────────────────────────

  const handleFrontImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFrontIdImage(URL.createObjectURL(file));
      setFrontIdFile(file);
    }
    e.target.value = '';
  };

  const handleBackImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBackIdImage(URL.createObjectURL(file));
      setBackIdFile(file);
    }
    e.target.value = '';
  };

  const handleRegistrationDocChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setRegistrationDocImage(URL.createObjectURL(file));
      setRegistrationDocFile(file);
    }
    e.target.value = '';
  };

  // ── Submit verification ───────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const currentUser = user;
      if (!currentUser) throw new Error('User not logged in');

      const verificationId = `ver_${Date.now()}_${currentUser.uid.substring(0, 6)}`;

      let frontIdUrl = null;
      let backIdUrl = null;
      let registrationDocUrl = null;

      if (isPersonal) {
        if (frontIdFile) {
          const frontRef = ref(storage, `verification/${verificationId}/front_id.jpg`);
          await uploadBytes(frontRef, frontIdFile);
          frontIdUrl = await getDownloadURL(frontRef);
        }
        if (backIdFile) {
          const backRef = ref(storage, `verification/${verificationId}/back_id.jpg`);
          await uploadBytes(backRef, backIdFile);
          backIdUrl = await getDownloadURL(backRef);
        }
      } else if (isBusiness) {
        if (registrationDocFile) {
          const docRef = ref(storage, `verification/${verificationId}/registration_document.jpg`);
          await uploadBytes(docRef, registrationDocFile);
          registrationDocUrl = await getDownloadURL(docRef);
        }
      }

      const verificationData = {
        verificationId,
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        sellerCategory: selectedCategory,
        categoryType: selectedCategoryType,
        physicalAddress: address.trim(),
        email: email.trim(),
        ...(isPersonal && { frontIdUrl, backIdUrl }),
        ...(isBusiness && { registrationDocUrl }),
        submittedAt: serverTimestamp(),
        status: 'pending',
        isVerifiedSeller: false,
      };

      // Save to sellerVerifications collection
      await setDoc(doc(db, 'sellerVerifications', verificationId), verificationData);

      // Also save to user profile for quick access
      await updateDoc(doc(db, 'users', currentUser.uid), {
        verification: {
          verificationId,
          sellerCategory: selectedCategory,
          categoryType: selectedCategoryType,
          physicalAddress: address.trim(),
          email: email.trim(),
          ...(isPersonal && { frontIdUrl, backIdUrl }),
          ...(isBusiness && { registrationDocUrl }),
          submittedAt: serverTimestamp(),
          status: 'pending',
          isVerifiedSeller: false,
        },
        hasCompletedVerification: true,
        verificationSubmittedAt: serverTimestamp(),
        isVerifiedSeller: false,
      });

      navigate(-1);
    } catch (err) {
      console.error('Verification error:', err);
      setError('Failed to submit verification. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render category selector ──────────────────────────────────────────

  const renderCategorySelector = () => (
    <div>
      <p className="text-white/70 text-xs font-medium mb-1.5">Seller Category</p>
      <button
        type="button"
        onClick={() => setShowCategoryPicker(true)}
        className="w-full px-3.5 py-3.5 rounded-xl flex items-center text-left"
        style={{
          backgroundColor: COLORS.cardLight,
          border: `${selectedCategory ? 1.2 : 1}px solid ${
            selectedCategory ? COLORS.orange : 'rgba(255,255,255,0.07)'
          }`,
        }}
      >
        <div className="flex-1 min-w-0">
          <p
            className="text-[15px] font-medium truncate"
            style={{ color: selectedCategory ? '#FFFFFF' : 'rgba(255,255,255,0.38)' }}
          >
            {selectedCategory || 'Select your category'}
          </p>
          {selectedCategoryType && (
            <p className="text-[11px] font-semibold mt-1" style={{ color: COLORS.orange }}>
              {isPersonal ? 'Personal' : 'Business & Organizations'}
            </p>
          )}
        </div>
        <ForwardIcon />
      </button>
    </div>
  );

  // ── Render image picker ───────────────────────────────────────────────

  const renderImagePicker = ({ label, image, onChange, icon, inputRef }) => (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      className="w-full p-3 rounded-xl flex items-center gap-3.5"
      style={{
        backgroundColor: COLORS.cardLight,
        border: `${image ? 1.5 : 1}px solid ${image ? COLORS.orange : 'rgba(255,255,255,0.12)'}`,
      }}
    >
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onChange} />
      <div
        className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: COLORS.card }}
      >
        {image ? (
          <img src={image} alt={label} className="w-full h-full object-cover" />
        ) : (
          icon
        )}
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="text-white text-sm font-medium">{label}</p>
        <p className="text-white/40 text-xs">{image ? 'Tap to change' : 'Tap to upload'}</p>
      </div>
      {image && <CheckCircleIcon />}
    </button>
  );

  // ── Render document section (personal vs business) ───────────────────

  const renderDocumentSection = () => {
    if (!isPersonal && !isBusiness) return null;

    if (isPersonal) {
      return (
        <>
          <div className="h-px bg-white/5 my-5" />
          <p className="text-white text-sm font-semibold mb-3">National ID</p>
          <div className="md:grid md:grid-cols-2 md:gap-4 space-y-4 md:space-y-0">
            {renderImagePicker({
              label: 'Front of National ID',
              image: frontIdImage,
              onChange: handleFrontImageChange,
              icon: <ContactPageIcon />,
              inputRef: frontInputRef,
            })}
            {renderImagePicker({
              label: 'Back of National ID',
              image: backIdImage,
              onChange: handleBackImageChange,
              icon: <ContactPageIcon />,
              inputRef: backInputRef,
            })}
          </div>
        </>
      );
    }

    // Business & Organizations
    return (
      <>
        <div className="h-px bg-white/5 my-5" />
        <p className="text-white text-sm font-semibold">Registration Document</p>
        <p className="text-white/40 text-xs mt-1 mb-3">
          Upload your Registration Document/Certificate
        </p>
        {renderImagePicker({
          label: 'Registration Document/Certificate',
          image: registrationDocImage,
          onChange: handleRegistrationDocChange,
          icon: <DescriptionIcon />,
          inputRef: regDocInputRef,
        })}
      </>
    );
  };

  // ── Render text field ─────────────────────────────────────────────────

  const renderTextField = ({ label, value, onChange, placeholder, icon, maxLines = 1, optional = false }) => (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-white/70 text-xs font-medium">{label}</span>
        {optional && <span className="text-white/40 text-[11px]">(Optional)</span>}
      </div>
      <div className="rounded-xl flex items-start overflow-hidden" style={{ backgroundColor: COLORS.cardLight }}>
        {icon && <span className="pl-3.5 pt-3.5 text-white/40 flex-shrink-0">{icon}</span>}
        {maxLines > 1 ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={2}
            className="flex-1 bg-transparent text-white text-sm outline-none px-3.5 py-3.5 placeholder:text-white/20 resize-none"
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-white text-sm outline-none px-3.5 py-3.5 placeholder:text-white/20"
          />
        )}
      </div>
    </div>
  );

  // ── Shared pieces (used by both layouts) ───────────────────────────────

  const WarningBanner = () => (
    <div
      className="p-4 rounded-2xl flex items-center gap-3.5"
      style={{
        backgroundColor: 'rgba(255,23,68,0.12)',
        border: '1px solid rgba(255,23,68,0.30)',
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'rgba(255,23,68,0.2)' }}
      >
        <WarningIcon />
      </div>
      <p className="flex-1 text-red-500 text-[13px] font-semibold leading-relaxed">
        Provision of wrong information will result to the permanent suspension of your channel!
      </p>
    </div>
  );

  const SubmitButton = () => (
    <button
      type="button"
      onClick={handleSubmit}
      disabled={isSubmitting}
      className="w-full h-[54px] rounded-2xl font-bold text-[15px] flex items-center justify-center gap-2.5 transition-opacity hover:opacity-90 disabled:opacity-40"
      style={{ backgroundColor: COLORS.orange, color: '#000000' }}
    >
      {isSubmitting ? (
        <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          <VerifiedIcon />
          Submit Verification
        </>
      )}
    </button>
  );

  const ErrorMessage = () =>
    error ? (
      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    ) : null;

  // ── Main render ───────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg }}>
      {/* ══════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (unchanged) — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* Top Bar */}
        <div className="pt-safe-top px-3 pb-1">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10"
              style={{ backgroundColor: COLORS.card }}
            >
              <BackIcon />
            </button>
            <span className="text-white text-xl font-bold">Seller Verification</span>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-8">
          <div className="mt-3">
            <WarningBanner />
          </div>

          {/* Form */}
          <div className="mt-5 p-5 rounded-2xl" style={{ backgroundColor: COLORS.card }}>
            <h3 className="text-white text-lg font-bold">Identity Verification</h3>
            <p className="text-white/40 text-xs mt-1">Verify your identity to start selling on Filatom</p>

            <div className="space-y-4 mt-5">
              {renderCategorySelector()}

              {renderTextField({
                label: 'Physical Address',
                value: address,
                onChange: setAddress,
                placeholder: 'e.g. Kampala, Nakawa, Plot 123',
                icon: <LocationIcon />,
                maxLines: 2,
              })}

              {renderTextField({
                label: 'Email Address',
                value: email,
                onChange: setEmail,
                placeholder: 'your@email.com (optional)',
                icon: <EmailIcon />,
                optional: true,
              })}
            </div>

            {renderDocumentSection()}
          </div>

          <div className="mt-4">
            <ErrorMessage />
          </div>

          <div className="mt-5">
            <SubmitButton />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (new) — visible at md breakpoint and up.
          Sticky header, then a centered form column — the address/email
          fields sit side by side to use the extra width, and the National
          ID front/back uploads run side by side too (handled inside
          renderDocumentSection via md:grid).
         ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <div className="sticky top-0 z-10 backdrop-blur-sm border-b border-white/10" style={{ backgroundColor: `${COLORS.bg}E6` }}>
          <div className="max-w-2xl mx-auto px-8 h-20 flex items-center gap-5">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
              style={{ backgroundColor: COLORS.card }}
              aria-label="Go back"
            >
              <BackIcon />
            </button>
            <div>
              <h1 className="text-white text-2xl font-bold leading-tight">Seller Verification</h1>
              <p className="text-white/40 text-sm mt-0.5">Verify your identity to start selling on Filatom</p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-8 py-10">
          <WarningBanner />

          {/* Form */}
          <div className="mt-5 p-6 rounded-2xl" style={{ backgroundColor: COLORS.card }}>
            <h3 className="text-white text-lg font-bold">Identity Verification</h3>
            <p className="text-white/40 text-xs mt-1">Fill in your details below</p>

            <div className="mt-5 space-y-4">
              {renderCategorySelector()}

              <div className="grid grid-cols-2 gap-4">
                {renderTextField({
                  label: 'Physical Address',
                  value: address,
                  onChange: setAddress,
                  placeholder: 'e.g. Kampala, Nakawa, Plot 123',
                  icon: <LocationIcon />,
                  maxLines: 2,
                })}

                {renderTextField({
                  label: 'Email Address',
                  value: email,
                  onChange: setEmail,
                  placeholder: 'your@email.com (optional)',
                  icon: <EmailIcon />,
                  optional: true,
                })}
              </div>
            </div>

            {renderDocumentSection()}
          </div>

          <div className="mt-4">
            <ErrorMessage />
          </div>

          <div className="mt-5">
            <SubmitButton />
          </div>
        </div>
      </div>

      {/* Category picker overlay — shared, not layout-specific */}
      {showCategoryPicker && (
        <CategorySelectionPage
          selectedCategory={selectedCategory}
          onSelect={handleCategorySelect}
          onClose={() => setShowCategoryPicker(false)}
        />
      )}
    </div>
  );
};

export default IdVerificationPage;